import React, { useState } from "react";

/**
 * ThermalExpansionLab
 * Interactive thermal-expansion simulation (ΔL = α·L₀·ΔT).
 *
 * Drop-in, self-contained: no external CSS import, no CDN scripts.
 * Styles are injected once and scoped under `.te-lab`, so they won't
 * collide with the rest of your app.
 *
 * Fonts: falls back to system fonts. For the intended look, load
 * Fraunces + IBM Plex Sans + IBM Plex Mono (e.g. a Google Fonts <link>
 * in your index.html). Dark mode follows prefers-color-scheme; you can
 * also force it by setting data-theme="dark" on the root element.
 *
 * Usage:
 *   import ThermalExpansionLab from "./ThermalExpansionLab";
 *   <ThermalExpansionLab />
 */

// Linear expansion coefficients, α in 1/°C
const MATERIALS = [
  { name: "Aluminum",         alpha: 24e-6 },
  { name: "Copper",           alpha: 17e-6 },
  { name: "Steel / iron",     alpha: 12e-6 },
  { name: "Concrete",         alpha: 12e-6 },
  { name: "Glass (ordinary)", alpha: 9e-6  },
];
const T_BASE = 20;            // °C, length measured here
const T_MIN = -20, T_MAX = 220;

function tempColor(t) {
  const f = Math.max(0, Math.min(1, (t - T_MIN) / (T_MAX - T_MIN)));
  const c = [42, 111, 151], h = [193, 88, 59];
  const mix = c.map((v, i) => Math.round(v + (h[i] - v) * f));
  return `rgb(${mix[0]},${mix[1]},${mix[2]})`;
}
function fmt(x, d) {
  return x.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

const STYLES = `
.te-lab {
  /* Layout: control panel + live bar/thermometer stage, data table below. */
  --bg: #f4f1ea; --surface: #ffffff; --ink: #1c2434; --muted: #5d6370;
  --line: #d9d3c6; --navy: #1f3864; --gold: #bf8f00; --cold: #2a6f97; --hot: #c1583b;
  --shadow: 0 1px 2px rgba(28,36,52,.06), 0 8px 24px rgba(28,36,52,.07);
  --disp: "Fraunces", Georgia, serif;
  --body: "IBM Plex Sans", system-ui, sans-serif;
  --mono: "IBM Plex Mono", ui-monospace, monospace;
  background: var(--bg); color: var(--ink); font-family: var(--body); line-height: 1.5;
}
@media (prefers-color-scheme: dark) {
  .te-lab:not([data-theme="light"]) {
    --bg: #141821; --surface: #1d222e; --ink: #e9ecf2; --muted: #9aa2b1;
    --line: #2e3542; --navy: #9fc0ef; --gold: #e3b94a; --cold: #5aa6cf; --hot: #e07a5c;
    --shadow: 0 1px 2px rgba(0,0,0,.3), 0 10px 28px rgba(0,0,0,.4);
    color-scheme: dark;
  }
}
.te-lab[data-theme="dark"] {
  --bg: #141821; --surface: #1d222e; --ink: #e9ecf2; --muted: #9aa2b1;
  --line: #2e3542; --navy: #9fc0ef; --gold: #e3b94a; --cold: #5aa6cf; --hot: #e07a5c;
  --shadow: 0 1px 2px rgba(0,0,0,.3), 0 10px 28px rgba(0,0,0,.4);
  color-scheme: dark;
}
.te-lab * { box-sizing: border-box; }
.te-lab .wrap { max-width: 920px; margin: 0 auto; padding: 28px 16px 56px; }
.te-lab h1 { font-family: var(--disp); font-weight: 600; font-size: clamp(1.7rem, 4vw, 2.5rem);
  color: var(--navy); margin: 0; letter-spacing: -.01em; text-wrap: balance; }
.te-lab .sub { color: var(--muted); margin: 6px 0 0; font-size: .98rem; max-width: 60ch; }
.te-lab .rule { height: 3px; background: var(--gold); border: 0; margin: 16px 0 24px; border-radius: 2px; }
.te-lab .card { background: var(--surface); border: 1px solid var(--line); border-radius: 14px;
  box-shadow: var(--shadow); padding: 20px; }
.te-lab .grid { display: grid; grid-template-columns: 1fr; gap: 20px; }
@media (min-width: 680px) { .te-lab .grid.two { grid-template-columns: 1.35fr 1fr; } }
.te-lab label.ctl { display: block; font-size: .8rem; text-transform: uppercase; letter-spacing: .06em;
  color: var(--muted); font-weight: 600; margin-bottom: 7px; }
.te-lab .field { margin-bottom: 18px; }
.te-lab .field:last-child { margin-bottom: 0; }
.te-lab select, .te-lab input[type="range"] { width: 100%; }
.te-lab select { font-family: var(--body); font-size: 1rem; padding: 9px 10px; border-radius: 9px;
  border: 1px solid var(--line); background: var(--surface); color: var(--ink); }
.te-lab input[type="range"] { accent-color: var(--gold); height: 26px; }
.te-lab .val { font-family: var(--mono); font-weight: 600; color: var(--ink); font-variant-numeric: tabular-nums; }
.te-lab .readrow { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; }
.te-lab .stage { display: flex; gap: 18px; align-items: stretch; }
.te-lab .barzone { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; justify-content: center; }
.te-lab .track { position: relative; overflow-x: auto; padding: 34px 6px 30px; }
.te-lab .anchor { position: relative; height: 54px; display: flex; align-items: center; }
.te-lab .wall { width: 10px; align-self: stretch; background: repeating-linear-gradient(45deg, var(--muted) 0 3px, transparent 3px 6px);
  border-left: 2px solid var(--ink); }
.te-lab .bar { height: 46px; border-radius: 3px; transition: width .35s ease, background-color .35s ease;
  position: relative; box-shadow: inset 0 2px 4px rgba(255,255,255,.25), inset 0 -3px 6px rgba(0,0,0,.18); }
.te-lab .ghost { position: absolute; top: 0; height: 46px; border: 2px dashed var(--muted); border-left: 0;
  border-radius: 3px; opacity: .7; }
.te-lab .dl-brace { position: absolute; top: -26px; height: 20px; border-left: 1px solid var(--hot);
  border-right: 1px solid var(--hot); border-top: 1px solid var(--hot); }
.te-lab .dl-lab { position: absolute; top: -26px; transform: translateX(-50%); font-family: var(--mono);
  font-size: .72rem; color: var(--hot); white-space: nowrap; font-weight: 600; }
.te-lab .scalecap { text-align: center; font-size: .78rem; color: var(--muted); margin-top: 4px; font-family: var(--mono); }
.te-lab .thermo { width: 46px; flex: 0 0 auto; display: flex; flex-direction: column; align-items: center; gap: 6px; }
.te-lab .tube { width: 18px; flex: 1 1 auto; min-height: 150px; border: 2px solid var(--line); border-radius: 10px;
  background: var(--bg); position: relative; overflow: hidden; }
.te-lab .merc { position: absolute; left: 0; right: 0; bottom: 0; transition: height .35s ease, background-color .35s ease; }
.te-lab .tnum { font-family: var(--mono); font-weight: 600; font-size: .9rem; font-variant-numeric: tabular-nums; }
.te-lab .formula { font-family: var(--mono); font-size: .92rem; background: var(--bg); border: 1px solid var(--line);
  border-radius: 10px; padding: 12px 14px; color: var(--ink); overflow-x: auto; white-space: nowrap;
  font-variant-numeric: tabular-nums; }
.te-lab .big { font-family: var(--disp); font-size: clamp(1.8rem, 5vw, 2.6rem); color: var(--hot); font-weight: 600; line-height: 1; }
.te-lab .biglab { font-size: .8rem; text-transform: uppercase; letter-spacing: .06em; color: var(--muted); font-weight: 600; }
.te-lab .btn { font-family: var(--body); font-size: .92rem; font-weight: 600; cursor: pointer; border-radius: 9px;
  padding: 10px 15px; border: 1px solid var(--navy); background: var(--navy); color: #fff; transition: filter .15s; }
.te-lab .btn:hover { filter: brightness(1.08); }
.te-lab .btn.ghostbtn { background: transparent; color: var(--navy); }
.te-lab .btn:focus-visible, .te-lab select:focus-visible, .te-lab input:focus-visible { outline: 2px solid var(--gold); outline-offset: 2px; }
.te-lab .btnrow { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 16px; }
.te-lab table { width: 100%; border-collapse: collapse; font-size: .9rem; font-variant-numeric: tabular-nums; }
.te-lab th, .te-lab td { text-align: right; padding: 8px 10px; border-bottom: 1px solid var(--line); }
.te-lab th:first-child, .te-lab td:first-child { text-align: left; }
.te-lab th { font-size: .74rem; text-transform: uppercase; letter-spacing: .05em; color: var(--muted); font-weight: 700; }
.te-lab td { font-family: var(--mono); }
.te-lab td.matname { font-family: var(--body); }
.te-lab .empty { color: var(--muted); font-style: italic; padding: 18px 0; text-align: center; }
.te-lab .tablewrap { overflow-x: auto; }
.te-lab .prompts { margin: 0; padding-left: 20px; color: var(--ink); }
.te-lab .prompts li { margin: 7px 0; }
.te-lab .sectitle { font-family: var(--disp); font-weight: 600; color: var(--navy); font-size: 1.15rem; margin: 0 0 4px; }
.te-lab .chip { display: inline-block; font-family: var(--mono); font-size: .78rem; padding: 2px 8px; border-radius: 999px;
  border: 1px solid var(--line); color: var(--muted); }
@media (prefers-reduced-motion: reduce) { .te-lab .bar, .te-lab .merc { transition: none; } }
`;

export default function ThermalExpansionLab() {
  const [matIdx, setMatIdx] = useState(0);
  const [L0, setL0] = useState(10);          // meters
  const [tFinal, setTFinal] = useState(120); // °C
  const [mag, setMag] = useState(100);       // visual magnification
  const [trials, setTrials] = useState([]);

  const mat = MATERIALS[matIdx];
  const dT = tFinal - T_BASE;
  const dL = mat.alpha * L0 * dT;             // meters (signed)
  const dL_mm = dL * 1000;
  const newLen = L0 + dL;
  const frac = mat.alpha * dT;                // fractional change ΔL/L0

  const BASE_PX = 320;
  const extraPx = frac * BASE_PX * mag;       // signed visual expansion
  const barPx = Math.max(24, BASE_PX + extraPx);
  const color = tempColor(tFinal);
  const mercH = ((tFinal - T_MIN) / (T_MAX - T_MIN)) * 100;
  const showGhost = Math.abs(extraPx) > 2;

  function record() {
    setTrials((t) => [...t, { id: Date.now(), mat: mat.name, L0, dT, dL_mm }]);
  }

  return (
    <div className="te-lab">
      <style>{STYLES}</style>
      <div className="wrap">
        <h1>Thermal Expansion Lab</h1>
        <p className="sub">
          Heat a bar of metal and watch it stretch. Pick a material, set its starting
          length, then drag the temperature. The change in length is computed live from
          ΔL = α·L₀·ΔT. Record each trial in the data table and look for the pattern.
        </p>
        <hr className="rule" />

        <div className="grid two">
          {/* ---- controls ---- */}
          <div className="card">
            <div className="field">
              <label className="ctl" htmlFor="te-mat">Material (α = expansion coefficient)</label>
              <select id="te-mat" value={matIdx} onChange={(e) => setMatIdx(+e.target.value)}>
                {MATERIALS.map((m, i) => (
                  <option key={m.name} value={i}>
                    {m.name} — α = {m.alpha * 1e6} × 10⁻⁶ /°C
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label className="ctl" htmlFor="te-len">
                Original length L₀ &nbsp;<span className="val">{fmt(L0, 1)} m</span>
              </label>
              <input id="te-len" type="range" min="0.5" max="50" step="0.5"
                value={L0} onChange={(e) => setL0(+e.target.value)} />
            </div>

            <div className="field">
              <label className="ctl" htmlFor="te-temp">
                Temperature &nbsp;<span className="val">{tFinal} °C</span>
                &nbsp;<span className="chip">ΔT = {dT >= 0 ? "+" : ""}{dT} °C from {T_BASE} °C</span>
              </label>
              <input id="te-temp" type="range" min={T_MIN} max={T_MAX} step="1"
                value={tFinal} onChange={(e) => setTFinal(+e.target.value)} />
            </div>

            <div className="field">
              <label className="ctl" htmlFor="te-mag">
                Visual magnification &nbsp;<span className="val">×{mag}</span>
              </label>
              <input id="te-mag" type="range" min="1" max="200" step="1"
                value={mag} onChange={(e) => setMag(+e.target.value)} />
            </div>
          </div>

          {/* ---- live readout ---- */}
          <div className="card" style={{ display: "flex", flexDirection: "column", gap: "14px", justifyContent: "center" }}>
            <div>
              <div className="biglab">Change in length (ΔL)</div>
              <div className="big">{dL_mm >= 0 ? "+" : "−"}{fmt(Math.abs(dL_mm), 2)} mm</div>
            </div>
            <div className="formula">
              ΔL = ({mat.alpha * 1e6}×10⁻⁶)({fmt(L0, 1)})({dT >= 0 ? "+" : ""}{dT}) = {fmt(dL, 5)} m
            </div>
            <div className="readrow">
              <span className="biglab">New length</span>
              <span className="val" style={{ fontSize: "1.05rem" }}>{fmt(newLen, 5)} m</span>
            </div>
            <div className="readrow">
              <span className="biglab">Fractional change</span>
              <span className="val">{fmt(frac * 100, 4)} %</span>
            </div>
          </div>
        </div>

        {/* ---- visual stage ---- */}
        <div className="card" style={{ marginTop: "20px" }}>
          <div className="stage">
            <div className="barzone">
              <div className="track">
                <div className="anchor">
                  <div className="wall" />
                  <div style={{ position: "relative" }}>
                    {showGhost && <div className="ghost" style={{ left: 0, width: BASE_PX + "px" }} />}
                    <div className="bar" style={{ width: barPx + "px", background: color }} />
                    {showGhost && extraPx > 0 && (
                      <>
                        <div className="dl-brace" style={{ left: BASE_PX + "px", width: extraPx + "px" }} />
                        <div className="dl-lab" style={{ left: (BASE_PX + extraPx / 2) + "px" }}>
                          ΔL = {dL_mm >= 0 ? "" : "−"}{fmt(Math.abs(dL_mm), 1)} mm
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="scalecap">
                bar fixed at the wall (left) · dashed = length at {T_BASE} °C · expansion shown ×{mag} actual size
              </div>
            </div>

            <div className="thermo">
              <div className="tnum" style={{ color }}>{tFinal}°</div>
              <div className="tube">
                <div className="merc" style={{ height: mercH + "%", background: color }} />
              </div>
              <div className="tnum" style={{ color: "var(--muted)", fontSize: ".72rem" }}>°C</div>
            </div>
          </div>

          <div className="btnrow">
            <button className="btn" onClick={record}>Record this trial</button>
            {trials.length > 0 && (
              <button className="btn ghostbtn" onClick={() => setTrials([])}>Clear table</button>
            )}
          </div>
        </div>

        {/* ---- data table ---- */}
        <div className="card" style={{ marginTop: "20px" }}>
          <h2 className="sectitle">Your data</h2>
          <div className="tablewrap">
            <table>
              <thead>
                <tr><th>Material</th><th>L₀ (m)</th><th>ΔT (°C)</th><th>ΔL (mm)</th></tr>
              </thead>
              <tbody>
                {trials.length === 0 && (
                  <tr><td colSpan="4" className="empty">No trials yet — set up a run and press “Record this trial.”</td></tr>
                )}
                {trials.map((r) => (
                  <tr key={r.id}>
                    <td className="matname">{r.mat}</td>
                    <td>{fmt(r.L0, 1)}</td>
                    <td>{r.dT >= 0 ? "+" : ""}{r.dT}</td>
                    <td>{r.dL_mm >= 0 ? "+" : "−"}{fmt(Math.abs(r.dL_mm), 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ---- investigate ---- */}
        <div className="card" style={{ marginTop: "20px" }}>
          <h2 className="sectitle">Investigate</h2>
          <ol className="prompts">
            <li>Keep L₀ and ΔT fixed. Record all five materials. Which expands the most? How does that line up with each α value?</li>
            <li>Pick one material. Double the temperature change — what happens to ΔL? Now double the original length. What pattern do you see?</li>
            <li>Drag the temperature below 20 °C. What happens to ΔL, and what does a negative answer mean physically?</li>
            <li>Set magnification to ×1 (true scale). Why do engineers still have to design for expansion even though it looks tiny here?</li>
          </ol>
        </div>
      </div>
    </div>
  );
}